import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  Mail,
  Plus,
  Sparkles,
  X,
} from 'lucide-react';
import { MarketingLayout } from '@/components/marketing/MarketingLayout';
import { cn } from '@/lib/utils';
import { detectBrowserTimeZone } from '@/lib/timezones';

/* -------------------------------------------------------------------------- */
/*  Step config                                                               */
/* -------------------------------------------------------------------------- */

type StepKey = 'account' | 'business' | 'subdomain' | 'area' | 'service' | 'theme';

const STEPS: { key: StepKey; label: string }[] = [
  { key: 'account', label: 'Account' },
  { key: 'business', label: 'Business' },
  { key: 'subdomain', label: 'Web address' },
  { key: 'area', label: 'Service area' },
  { key: 'service', label: 'First service' },
  { key: 'theme', label: 'Theme' },
];

const CURRENCIES = [
  { code: 'GBP', symbol: '£', label: 'GBP (£)' },
  { code: 'USD', symbol: '$', label: 'USD ($)' },
  { code: 'EUR', symbol: '€', label: 'EUR (€)' },
  { code: 'AUD', symbol: 'A$', label: 'AUD (A$)' },
  { code: 'CAD', symbol: 'C$', label: 'CAD (C$)' },
];

const detectCurrency = (): string => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz === 'Europe/London' || tz === 'Europe/Belfast') return 'GBP';
    if (tz.startsWith('Australia/')) return 'AUD';
    if (tz.startsWith('America/Toronto') || tz.startsWith('America/Vancouver') ||
        tz.startsWith('America/Winnipeg') || tz.startsWith('America/Halifax') ||
        tz.startsWith('America/Edmonton') || tz.startsWith('America/Regina')) return 'CAD';
    if (tz.startsWith('Europe/')) return 'EUR';
    if (tz.startsWith('America/')) return 'USD';
  } catch { /* ignore */ }
  return 'GBP';
};

const SIGNUP_THEMES = [
  { id: 'classic', name: 'Classic Professional', description: 'Default — same look as the demo site', primary: '#66499c', bg: '#f5f3f7' },
  { id: 'modern-minimal', name: 'Modern Minimal', description: 'Ultra-clean with bold typography and sharp edges', primary: '#1a1a1a', bg: '#f8f8f8' },
  { id: 'warm-welcome', name: 'Warm & Welcoming', description: 'Friendly terracotta tones with organic shapes', primary: '#c4704a', bg: '#fdf8f4' },
  { id: 'bold-vibrant', name: 'Bold & Geometric', description: 'High-energy with sharp lines and strong contrast', primary: '#2563eb', bg: '#eff6ff' },
  { id: 'elegant-serif', name: 'Elegant Serif', description: 'Sophisticated with classic serif typography', primary: '#4a4a4a', bg: '#fafafa' },
];

interface WizardData {
  fullName: string;
  email: string;
  password: string;
  businessName: string;
  subdomain: string;
  serviceArea: string;
  firstServiceName: string;
  firstServicePrice: string;
  currency: string;
  theme: string;
  timezone: string;
}

const EMPTY: WizardData = {
  fullName: '',
  email: '',
  password: '',
  businessName: '',
  subdomain: '',
  serviceArea: '',
  firstServiceName: '',
  firstServicePrice: '',
  currency: detectCurrency(),
  theme: 'classic',
  timezone: detectBrowserTimeZone(),
};

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

const normalizeEmail = (email: string | null | undefined): string =>
  (email ?? '').trim().toLowerCase();

// Issue #6: the email a confirmation link was sent to is persisted so the
// binding between "the email being confirmed" and "the session that finishes
// the wizard" survives the magic-link reload. The org must only ever be
// provisioned under the account that owns this email.
const PENDING_CONFIRMATION_KEY = 'signup.pendingConfirmationEmail';

const readPendingConfirmation = (): string | null => {
  try {
    return localStorage.getItem(PENDING_CONFIRMATION_KEY);
  } catch {
    return null;
  }
};

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);

const accountSchema = z.object({
  email: z.string().trim().email('Please enter a valid email').max(255),
  password: z.string().min(8, 'Password must be at least 8 characters').max(72),
});

const RESERVED = new Set([
  'www', 'app', 'api', 'admin', 'mail', 'm', 'demo', 'auth', 'signup',
  'register', 'pricing', 'faq', 'about', 'contact', 'support', 'help',
]);

const appendServiceArea = (selectedArea: string, currentValue: string): string => {
  const selected = selectedArea.trim();
  const hadTrailingComma = currentValue.trim().endsWith(',');
  const areas = currentValue
    .split(',')
    .map((area) => area.trim())
    .filter(Boolean);

  if (!selected) return areas.join(', ');
  if (areas.some((area) => area.toLowerCase() === selected.toLowerCase())) {
    return areas.join(', ');
  }

  if (areas.length > 0 && !hadTrailingComma) {
    return [...areas.slice(0, -1), selected].join(', ');
  }

  return [...areas, selected].join(', ');
};

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

const Signup = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, organizationMembership, refreshOrgMembership, signOut } = useAuth();

  const [stepIndex, setStepIndex] = useState(0);
  const [data, setData] = useState<WizardData>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [completedSubdomain, setCompletedSubdomain] = useState<string | null>(null);
  // Set when the confirmation email has been sent and we're awaiting the user.
  // Initialised from storage so a magic-link reload restores the pending state
  // (and the identity guard that goes with it).
  const [confirmationEmail, setConfirmationEmail] = useState<string | null>(readPendingConfirmation);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  // Always write the pending email through these helpers so the persisted
  // marker stays in lock-step with the in-memory state.
  const startPendingConfirmation = (email: string) => {
    try {
      localStorage.setItem(PENDING_CONFIRMATION_KEY, email);
    } catch { /* ignore */ }
    setConfirmationEmail(email);
  };
  const clearPendingConfirmation = () => {
    try {
      localStorage.removeItem(PENDING_CONFIRMATION_KEY);
    } catch { /* ignore */ }
    setConfirmationEmail(null);
  };

  useEffect(() => {
    if (!confirmationEmail) {
      const pending = readPendingConfirmation();
      if (pending) setConfirmationEmail(pending);
    }
  }, [confirmationEmail]);

  // If already logged in with a membership → skip to admin (return user), unless
  // a different signup confirmation is pending. In that case the check-email
  // guard below must win so a stale session can't hijack provisioning.
  useEffect(() => {
    const pendingConfirmation = confirmationEmail ?? readPendingConfirmation();
    if (!authLoading && user && organizationMembership && !pendingConfirmation && !completedSubdomain) {
      navigate('/admin');
    }
  }, [authLoading, user, organizationMembership, confirmationEmail, navigate, completedSubdomain]);

  // The email this signup is being provisioned for: the address a confirmation
  // link was sent to, or — failing that — whatever was typed on the account
  // step. Null for a signed-in user with no pending confirmation (a returning
  // owner adding another business).
  const intendedEmail = confirmationEmail ?? (data.email.trim() || null);

  // Resume / identity guard once a user is signed in (issue #6). Provisioning
  // binds the org to the authenticated JWT, so the session that finishes the
  // wizard MUST belong to the email being confirmed — otherwise the business is
  // created under the wrong account.
  useEffect(() => {
    if (!user || completedSubdomain) return;

    if (confirmationEmail) {
      if (normalizeEmail(user.email) === normalizeEmail(confirmationEmail)) {
        // The right person clicked their magic link: consume the pending marker
        // and let the wizard advance below on the next render.
        try {
          localStorage.removeItem(PENDING_CONFIRMATION_KEY);
        } catch { /* ignore */ }
        setConfirmationEmail(null);
      }
      // A confirmation is still pending (matched-and-just-cleared, or a stale
      // mismatched session). Either way, don't advance this render — the
      // check-email panel stays up and provisioning is unreachable.
      return;
    }

    // No pending confirmation: a signed-in user (fresh magic-link resume or a
    // returning owner) advances into business setup.
    if (stepIndex === 0) setStepIndex(1);
  }, [user, confirmationEmail, completedSubdomain, stepIndex]);

  const currentStep = STEPS[stepIndex];

  const update = <K extends keyof WizardData>(key: K, value: WizardData[K]) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  /* ----------------------------- Step navigation ---------------------------- */

  const goNext = () => setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  /* ----------------------------- Step submitters ---------------------------- */

  const handleAccountSubmit = async () => {
    const name = data.fullName.trim();
    if (name.length < 2) {
      toast.error('Please enter your name (at least 2 characters)');
      return;
    }
    const parsed = accountSchema.safeParse({ email: data.email, password: data.password });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setSubmitting(true);
    try {
      // Issue #6: if a *different* account is already signed in (a stale
      // session), clear it before sending the confirmation. Otherwise the
      // wizard would carry that session through to provisioning and create the
      // business under the wrong user — the confirmation link, sent to the
      // typed email, is what must establish the owning identity.
      if (user && normalizeEmail(user.email) !== normalizeEmail(parsed.data.email)) {
        await signOut();
      }
      // Issue #5: Supabase Auth's default mailer was not delivering signup
      // confirmation emails. Send the confirmation through the app's existing
      // Resend provider via the Edge Function instead of the Supabase mailer.
      // The emailed link is a secure magic sign-in link that lands back on
      // /signup, so clicking it both proves email ownership and resumes the
      // wizard at the business step.
      const redirectTo = `${window.location.origin}/signup`;
      const { data: fnData, error } = await supabase.functions.invoke(
        'send-business-signup-confirmation',
        {
          body: {
            email: parsed.data.email,
            password: parsed.data.password,
            name,
            mode: 'signup',
            redirectTo,
          },
        },
      );
      if (error) {
        toast.error(error.message || 'Could not send confirmation email. Please try again.');
        return;
      }
      if (fnData?.error) {
        toast.error(fnData.error);
        return;
      }
      // A secure magic sign-in link is on its way (new, returning, or already
      // confirmed accounts alike). Show the check-email panel — do NOT advance;
      // clicking the email signs them in and resumes the wizard on /signup.
      setResent(false);
      startPendingConfirmation(parsed.data.email);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!confirmationEmail) return;
    setResending(true);
    try {
      const redirectTo = `${window.location.origin}/signup`;
      const { data: fnData, error } = await supabase.functions.invoke(
        'send-business-signup-confirmation',
        {
          body: {
            email: confirmationEmail,
            password: data.password,
            mode: 'resend',
            redirectTo,
          },
        },
      );
      if (error) {
        toast.error(error.message || 'Could not resend confirmation email.');
        return;
      }
      if (fnData?.error) {
        toast.error(fnData.error);
        return;
      }
      setResent(true);
      toast.success('Confirmation email sent. Please check your inbox.');
    } finally {
      setResending(false);
    }
  };

  const handleBusinessSubmit = () => {
    const name = data.businessName.trim();
    if (name.length < 2) {
      toast.error('Please enter your business name (at least 2 characters)');
      return;
    }
    // Pre-fill subdomain suggestion
    if (!data.subdomain) {
      update('subdomain', slugify(name));
    }
    goNext();
  };

  const handleSubdomainSubmit = async () => {
    const sub = slugify(data.subdomain);
    if (sub.length < 3) {
      toast.error('Web address must be at least 3 characters');
      return;
    }
    if (RESERVED.has(sub)) {
      toast.error('That web address is reserved. Please pick another.');
      return;
    }
    setSubmitting(true);
    try {
      const { data: existing } = await supabase
        .from('organizations')
        .select('id')
        .eq('slug', sub)
        .maybeSingle();
      if (existing) {
        toast.error('That web address is already taken. Try another.');
        return;
      }
      update('subdomain', sub);
      goNext();
    } finally {
      setSubmitting(false);
    }
  };

  const handleAreaSubmit = () => {
    if (data.serviceArea.trim().length < 2) {
      toast.error('Please enter at least one service area');
      return;
    }
    goNext();
  };

  const handleServiceSubmit = () => {
    if (data.firstServiceName.trim().length < 2) {
      toast.error('Please enter a service name');
      return;
    }
    goNext();
  };

  /* ------------------------------ Final submit ----------------------------- */

  const handleFinalSubmit = async () => {
    if (!user) {
      toast.error('Session expired. Please start over.');
      setStepIndex(0);
      return;
    }

    // Issue #6: provisioning binds the org to the currently authenticated JWT.
    // Only proceed when that identity matches the email this signup was started
    // for. If a stale/different session is somehow still active, refuse and send
    // them back to confirm with the right email rather than creating the
    // business under the wrong account.
    if (intendedEmail && normalizeEmail(user.email) !== normalizeEmail(intendedEmail)) {
      toast.error(
        `Please sign in with ${intendedEmail} to finish — that's the email this business is being set up with.`,
      );
      startPendingConfirmation(intendedEmail);
      setResent(false);
      return;
    }

    setSubmitting(true);
    try {
      const { data: result, error } = await supabase.functions.invoke('provision-org', {
        body: {
          businessName:      data.businessName.trim(),
          subdomain:         data.subdomain,
          serviceArea:       data.serviceArea,
          firstServiceName:  data.firstServiceName.trim(),
          firstServicePrice: data.firstServicePrice.trim(),
          currency:          data.currency,
          theme:             data.theme,
          timezone:          data.timezone,
        },
      });

      if (error) throw error;
      if (result?.error) throw new Error(result.error);

      await refreshOrgMembership();
      clearPendingConfirmation();
      setCompletedSubdomain(result.subdomain);
      toast.success('Your site is live!');
    } catch (err) {
      console.error('Signup provisioning error:', err);
      toast.error(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /* --------------------------------- Loading -------------------------------- */

  if (authLoading) {
    return (
      <MarketingLayout tone="light">
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MarketingLayout>
    );
  }

  /* ------------------------------ Welcome screen ---------------------------- */

  if (completedSubdomain) {
    return (
      <MarketingLayout tone="light">
        <WelcomeScreen
          subdomain={completedSubdomain}
          businessName={data.businessName}
          onContinue={() => navigate('/admin')}
        />
      </MarketingLayout>
    );
  }

  /* -------------------------- Confirmation pending -------------------------- */

  if (confirmationEmail) {
    // If a *different* account is signed in, surface it: that session can't be
    // used to finish this signup (issue #6) — the link must be opened as the
    // confirmed email.
    const signedInMismatchEmail =
      user && normalizeEmail(user.email) !== normalizeEmail(confirmationEmail)
        ? (user.email ?? null)
        : null;
    return (
      <MarketingLayout tone="light">
        <ConfirmationPending
          email={confirmationEmail}
          signedInMismatchEmail={signedInMismatchEmail}
          resending={resending}
          resent={resent}
          onResend={handleResendConfirmation}
          onUseDifferentEmail={() => {
            clearPendingConfirmation();
            setResent(false);
          }}
        />
      </MarketingLayout>
    );
  }

  /* --------------------------------- Wizard --------------------------------- */

  return (
    <MarketingLayout tone="light">
      <section className="container mx-auto px-4 py-12 md:py-16">
        <div className="mx-auto max-w-2xl">
          <ProgressBar current={stepIndex} />

          <div className="mt-10 rounded-3xl border border-border bg-card p-6 shadow-sm md:p-10">
            {currentStep.key === 'account' && (
              <AccountStep
                data={data}
                update={update}
                submitting={submitting}
                onSubmit={handleAccountSubmit}
              />
            )}

            {currentStep.key === 'business' && (
              <BusinessStep
                data={data}
                update={update}
                onSubmit={handleBusinessSubmit}
                onBack={goBack}
              />
            )}

            {currentStep.key === 'subdomain' && (
              <SubdomainStep
                data={data}
                update={update}
                submitting={submitting}
                onSubmit={handleSubdomainSubmit}
                onBack={goBack}
              />
            )}

            {currentStep.key === 'area' && (
              <AreaStep
                data={data}
                update={update}
                onSubmit={handleAreaSubmit}
                onBack={goBack}
              />
            )}

            {currentStep.key === 'service' && (
              <FirstServiceStep
                data={data}
                update={update}
                submitting={submitting}
                onSubmit={handleServiceSubmit}
                onBack={goBack}
              />
            )}

            {currentStep.key === 'theme' && (
              <ThemeStep
                data={data}
                update={update}
                submitting={submitting}
                onSubmit={handleFinalSubmit}
                onBack={goBack}
              />
            )}
          </div>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/auth" className="font-semibold text-primary underline underline-offset-4 hover:text-primary/80">
              Sign in here
            </Link>
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default Signup;

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                            */
/* -------------------------------------------------------------------------- */

function ProgressBar({ current }: { current: number }) {
  return (
    <div className="flex items-center justify-between gap-2">
      {STEPS.map((step, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={step.key} className="flex flex-1 items-center gap-2">
            <div
              className={cn(
                'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold transition-colors',
                done && 'border-primary bg-primary text-primary-foreground',
                active && !done && 'border-primary text-primary',
                !done && !active && 'border-border text-muted-foreground'
              )}
            >
              {done ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={cn(
                  'h-1 flex-1 rounded-full transition-colors',
                  i < current ? 'bg-primary' : 'bg-border'
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function StepHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="mb-8 text-center">
      <h1 className="font-display text-3xl md:text-4xl">{title}</h1>
      <p className="mt-3 text-muted-foreground">{subtitle}</p>
    </div>
  );
}

function FormActions({
  onBack,
  submitLabel,
  submitting,
  onSubmit,
}: {
  onBack?: () => void;
  submitLabel: string;
  submitting?: boolean;
  onSubmit: () => void;
}) {
  return (
    <div className="mt-8 flex items-center justify-between gap-3">
      {onBack ? (
        <Button type="button" variant="ghost" onClick={onBack} disabled={submitting}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </Button>
      ) : (
        <span />
      )}
      <Button type="submit" onClick={onSubmit} disabled={submitting} className="rounded-full px-7">
        {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {submitLabel}
        {!submitting && <ArrowRight className="ml-2 h-4 w-4" />}
      </Button>
    </div>
  );
}

/* -------------------------- Confirmation pending -------------------------- */

function ConfirmationPending({
  email,
  signedInMismatchEmail,
  resending,
  resent,
  onResend,
  onUseDifferentEmail,
}: {
  email: string;
  signedInMismatchEmail?: string | null;
  resending: boolean;
  resent: boolean;
  onResend: () => void;
  onUseDifferentEmail: () => void;
}) {
  return (
    <section className="container mx-auto px-4 py-12 md:py-16">
      <div className="mx-auto max-w-md rounded-3xl border border-border bg-card p-8 text-center shadow-sm md:p-10">
        <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <Mail className="h-7 w-7 text-primary" />
        </div>
        <h1 className="font-display text-3xl md:text-4xl">Confirm your email</h1>
        <p className="mt-3 text-muted-foreground">
          We've sent a confirmation link to{' '}
          <span className="font-semibold text-foreground">{email}</span>. Click the link
          in that email to verify your account, then come back here to finish setting up
          your business.
        </p>
        <p className="mt-4 text-sm text-muted-foreground">
          Didn't get it? Check your spam folder, or resend it below.
        </p>

        {signedInMismatchEmail && (
          <p className="mt-4 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
            You're currently signed in as{' '}
            <span className="font-semibold">{signedInMismatchEmail}</span>. Open the link from{' '}
            <span className="font-semibold">{email}</span> to finish setting up this business under
            the right account.
          </p>
        )}

        <Button
          type="button"
          onClick={onResend}
          disabled={resending}
          className="mt-6 w-full rounded-full"
        >
          {resending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Resend confirmation email
        </Button>

        {resent && (
          <p className="mt-4 flex items-center justify-center gap-2 text-sm font-medium text-primary">
            <CheckCircle2 className="h-4 w-4" />
            Confirmation link sent. Check your inbox.
          </p>
        )}

        <button
          type="button"
          onClick={onUseDifferentEmail}
          className="mt-6 text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
        >
          Use a different email
        </button>
      </div>
    </section>
  );
}

/* ------------------------------ Step 1: Account --------------------------- */

function AccountStep({
  data,
  update,
  submitting,
  onSubmit,
}: {
  data: WizardData;
  update: <K extends keyof WizardData>(k: K, v: WizardData[K]) => void;
  submitting: boolean;
  onSubmit: () => void;
}) {
  const [showPassword, setShowPassword] = useState(false);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <StepHeader
        title="Create your account"
        subtitle="Free for 30 days. Cancel any time."
      />
      <div className="space-y-4">
        <div>
          <Label htmlFor="fullName">Your name</Label>
          <Input
            id="fullName"
            autoFocus
            autoComplete="name"
            placeholder="e.g. Jane Smith"
            value={data.fullName}
            onChange={(e) => update('fullName', e.target.value)}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={data.email}
            onChange={(e) => update('email', e.target.value)}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <div className="relative mt-1.5">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="At least 8 characters"
              value={data.password}
              onChange={(e) => update('password', e.target.value)}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
      <FormActions submitLabel="Continue" submitting={submitting} onSubmit={onSubmit} />
    </form>
  );
}

/* ------------------------------ Step 2: Business -------------------------- */

function BusinessStep({
  data,
  update,
  onSubmit,
  onBack,
}: {
  data: WizardData;
  update: <K extends keyof WizardData>(k: K, v: WizardData[K]) => void;
  onSubmit: () => void;
  onBack: () => void;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <StepHeader
        title="What's your business called?"
        subtitle="This is what customers will see across your site."
      />
      <div>
        <Label htmlFor="biz">Business name</Label>
        <Input
          id="biz"
          autoFocus
          placeholder="e.g. Cardiff Sparkle Cleaners"
          value={data.businessName}
          onChange={(e) => update('businessName', e.target.value)}
          className="mt-1.5"
        />
      </div>
      <FormActions onBack={onBack} submitLabel="Continue" onSubmit={onSubmit} />
    </form>
  );
}

/* ------------------------------ Step 3: Subdomain ------------------------- */

function SubdomainStep({
  data,
  update,
  submitting,
  onSubmit,
  onBack,
}: {
  data: WizardData;
  update: <K extends keyof WizardData>(k: K, v: WizardData[K]) => void;
  submitting: boolean;
  onSubmit: () => void;
  onBack: () => void;
}) {
  const cleaned = useMemo(() => slugify(data.subdomain), [data.subdomain]);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <StepHeader
        title="Pick your web address"
        subtitle="You'll get this instantly. You can connect a custom domain from your settings later."
      />
      <div>
        <Label htmlFor="sub">Your web address</Label>
        <div className="mt-1.5 flex items-center overflow-hidden rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring">
          <input
            id="sub"
            autoFocus
            placeholder="acme"
            value={data.subdomain}
            onChange={(e) => update('subdomain', e.target.value)}
            className="flex-1 bg-transparent px-3 py-2 text-base outline-none md:text-sm"
          />
          <span className="border-l border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
            .thepolishedversion.com
          </span>
        </div>
        {cleaned && cleaned !== data.subdomain && (
          <p className="mt-2 text-xs text-muted-foreground">
            Will be saved as <span className="font-medium text-foreground">{cleaned}</span>
          </p>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          Lowercase letters, numbers and dashes only.
        </p>
      </div>
      <FormActions
        onBack={onBack}
        submitLabel="Check and continue"
        submitting={submitting}
        onSubmit={onSubmit}
      />
    </form>
  );
}

/* ------------------------------ Step 4: Area ------------------------------ */

function AreaStep({
  data,
  update,
  onSubmit,
  onBack,
}: {
  data: WizardData;
  update: <K extends keyof WizardData>(k: K, v: WizardData[K]) => void;
  onSubmit: () => void;
  onBack: () => void;
}) {
  const [input, setInput] = useState('');

  const areas = data.serviceArea
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const setAreas = (next: string[]) => {
    update('serviceArea', next.join(', '));
  };

  const addArea = (rawValue?: string) => {
    const value = (rawValue ?? input).trim();
    if (!value) return;
    if (areas.includes(value)) {
      toast.error('That area is already in the list');
      setInput('');
      return;
    }
    setAreas([...areas, value]);
    setInput('');
  };

  const removeArea = (index: number) => {
    setAreas(areas.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    // also add whatever's typed but not yet submitted
    const pending = input.trim();
    if (pending && !areas.includes(pending)) {
      setAreas([...areas, pending]);
      setInput('');
    }
    onSubmit();
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <StepHeader
        title="Where do you work?"
        subtitle="The towns or cities you cover. Add more later from the admin."
      />
      <div className="space-y-3">
        <Label htmlFor="area">Service areas</Label>

        {areas.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {areas.map((area, index) => (
              <span
                key={`${area}-${index}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-sm font-medium text-secondary-foreground"
              >
                {area}
                <button
                  type="button"
                  onClick={() => removeArea(index)}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                  aria-label={`Remove ${area}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <AddressAutocomplete
            id="area"
            placeholder="Search a town or city..."
            value={input}
            onChange={setInput}
            onPlaceSelect={(place) => addArea(place.formattedAddress.split(',')[0].trim())}
            countries={["gb"]}
            type="regions"
            className="flex-1 min-w-0"
          />
          <Button type="button" variant="outline" onClick={() => addArea()}>
            <Plus className="h-4 w-4 mr-1.5" />
            Add
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          Pick from Google Places, or type and click Add. Remove by clicking the × on a tag.
        </p>
      </div>
      <FormActions onBack={onBack} submitLabel="Continue" onSubmit={handleSubmit} />
    </form>
  );
}

/* --------------------------- Step 5: First service ------------------------ */

function FirstServiceStep({
  data,
  update,
  submitting,
  onSubmit,
  onBack,
}: {
  data: WizardData;
  update: <K extends keyof WizardData>(k: K, v: WizardData[K]) => void;
  submitting: boolean;
  onSubmit: () => void;
  onBack: () => void;
}) {
  const currencySymbol = CURRENCIES.find((c) => c.code === data.currency)?.symbol ?? '£';
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <StepHeader
        title="Add your first service"
        subtitle="We'll create this plus a couple of placeholders so your site looks alive."
      />
      <div className="space-y-4">
        <div>
          <Label htmlFor="currency">Currency</Label>
          <select
            id="currency"
            value={data.currency}
            onChange={(e) => update('currency', e.target.value)}
            className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted-foreground">You can change this later in your admin settings.</p>
        </div>
        <div>
          <Label htmlFor="svcName">Service name</Label>
          <Input
            id="svcName"
            autoFocus
            placeholder="e.g. Regular Home Clean"
            value={data.firstServiceName}
            onChange={(e) => update('firstServiceName', e.target.value)}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="svcPrice">Price (optional)</Label>
          <Input
            id="svcPrice"
            placeholder={`e.g. ${currencySymbol}25/hr or From ${currencySymbol}80`}
            value={data.firstServicePrice}
            onChange={(e) => update('firstServicePrice', e.target.value)}
            className="mt-1.5"
          />
          <p className="mt-2 text-xs text-muted-foreground">
            Leave blank and we'll use a sensible default. Edit anytime in the admin.
          </p>
        </div>
      </div>
      <FormActions
        onBack={onBack}
        submitLabel={submitting ? 'Creating your site' : 'Launch my site'}
        submitting={submitting}
        onSubmit={onSubmit}
      />
    </form>
  );
}

/* ------------------------------ Step 6: Theme ----------------------------- */

function ThemeStep({
  data,
  update,
  submitting,
  onSubmit,
  onBack,
}: {
  data: WizardData;
  update: <K extends keyof WizardData>(k: K, v: WizardData[K]) => void;
  submitting: boolean;
  onSubmit: () => void;
  onBack: () => void;
}) {
  return (
    <div>
      <StepHeader
        title="Pick your look"
        subtitle="Choose a theme for your site. You can change it anytime from your admin."
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {SIGNUP_THEMES.map((theme) => {
          const selected = data.theme === theme.id;
          return (
            <button
              key={theme.id}
              type="button"
              onClick={() => update('theme', theme.id)}
              className={cn(
                'text-left rounded-xl border-2 p-4 transition-all',
                selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
              )}
            >
              <div className="flex gap-2 mb-3">
                <div className="h-8 w-8 rounded-full border border-border/50" style={{ background: theme.primary }} />
                <div className="h-8 flex-1 rounded-lg border border-border/50" style={{ background: theme.bg }} />
              </div>
              <p className="text-sm font-semibold text-foreground">{theme.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{theme.description}</p>
            </button>
          );
        })}
      </div>
      <FormActions
        onBack={onBack}
        submitLabel={submitting ? 'Creating your site…' : 'Launch my site'}
        submitting={submitting}
        onSubmit={onSubmit}
      />
    </div>
  );
}

/* ------------------------------ Welcome screen ---------------------------- */

function WelcomeScreen({
  subdomain,
  businessName,
  onContinue,
}: {
  subdomain: string;
  businessName: string;
  onContinue: () => void;
}) {
  const [countdown, setCountdown] = useState(5);
  const url = `${subdomain}.thepolishedversion.com`;

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((n) => {
        if (n <= 1) { clearInterval(interval); onContinue(); }
        return n - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [onContinue]);

  return (
    <section className="container mx-auto px-4 py-20 md:py-28">
      <div className="mx-auto max-w-xl text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <CheckCircle2 className="h-8 w-8 text-primary" />
        </div>
        <h1 className="mt-6 font-display text-4xl md:text-5xl">
          {businessName} is live.
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          Your booking site is ready. Taking you to your admin in {countdown}…
        </p>

        <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="font-medium">{url}</span>
        </div>

        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button size="lg" onClick={onContinue} className="rounded-full px-7">
            Go to admin now <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button asChild size="lg" variant="outline" className="rounded-full px-7">
            <a href={`https://${url}`} target="_blank" rel="noreferrer">
              View my live site
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}
